import { FormFieldTypes } from "@repo/design-system/components/forms/libs";

export const getValidationRules = (sections, t) => {
    let valRules = {};
    sections && sections.forEach(section => {
        const processField = (field) => {
            let rulesList = field.validation ? [...field.validation] : [];

            if (field.fieldType === FormFieldTypes.DATEPICKER) {
                if (field.fieldSetting?.maxDate || field.fieldSetting?.maxDateFrom) {
                    if (!rulesList.some(v => v.type === 'maxDate')) {
                        rulesList.push({ type: 'maxDate' });
                    }
                }
                if (field.fieldSetting?.minDate || field.fieldSetting?.minDateFrom) {
                    if (!rulesList.some(v => v.type === 'minDate')) {
                        rulesList.push({ type: 'minDate' });
                    }
                }
            }

            if (field.fieldSetting?.masked?.format && !rulesList.some(v => v.type === 'maskFormat')) {
                rulesList.push({ type: 'maskFormat' });
            }

            if (field.fieldType === FormFieldTypes.FILE && !rulesList.some(v => v.type === 'file')) {
                rulesList.push({ type: 'file' });
            }

            if (field.fieldType === FormFieldTypes.IMAGE && !rulesList.some(v => v.type === 'image')) {
                rulesList.push({ type: 'image' });
            }

            if (rulesList.length > 0) {
                let fieldValRules = rulesList.map(valItem => getValidationRule(field, valItem, t));
                valRules[field.dataField] = fieldValRules;
            }
        };

        if (section.rowGroup && section.rowGroup.length > 0) {
            section.rowGroup.forEach(rowGroup => {
                rowGroup.fields.forEach(processField);
            });
        } else if (section.fields && section.fields.length > 0) {
            section.fields.forEach(processField);
        }
    });

    return valRules;
};

const getValidationRule = (element, validation, t) => {
    let valObj = {};

    // Translate Field_Name if it's an i18n key (contains a dot), otherwise use as-is
    const fieldName = t && element.Field_Name && element.Field_Name.includes(".")
        ? t(element.Field_Name)
        : (element.Field_Name ?? "");

    const getMessage = (key, fallback) => {
        if (t) {
            const translated = t(`validation.${key}`);
            if (translated && translated !== `validation.${key}`) {
                return translated;
            }
        }
        return fallback;
    };

    switch (validation.type) {
        case "require":
            valObj = {
                type: validation.type,
                message: (element?.fieldType === FormFieldTypes.SELECT
                    || element?.fieldType === FormFieldTypes.TIME
                    || element?.fieldType === FormFieldTypes.DATEPICKER) ?
                    getMessage('select_field_required', "validation.select_field_required").replace('{placeholder}', fieldName)
                    :
                    getMessage('field_required', "validation.field_required").replace('{placeholder}', fieldName)
            };
            break;
        case "email":
            valObj = {
                type: validation.type,
                message: getMessage('email_invalid', "validation.email_invalid")
            };
            break;
        case "password":
            valObj = {
                type: validation.type,
                message: getMessage('pattern_invalid', "validation.pattern_invalid").replace("{0}", fieldName)
            };
            break;
        case "gradingPoint":
            valObj = {
                type: validation.type,
                maxValue: element?.fieldSetting?.maxValue,
                gradePointFormat: element?.fieldSetting?.gradePointFormat || element?.fieldSetting?.masked?.format,
                message: getMessage('grade_point_invalid', "validation.grade_point_invalid").replace("{0}", fieldName)
            };
            break;
        case "compare":
            valObj = {
                type: validation.type,
                compareEle: validation.compareEle,
                message: getMessage('field_mismatch', "validation.field_mismatch").replace("{0}", fieldName)
            };
            break;
        case "website":
            valObj = {
                type: validation.type,
                message: getMessage('website_invalid', "validation.website_invalid").replace("{0}", "Website URL")
            }
            break;
        case "onlyText":
            valObj = {
                type: validation.type,
                message: getMessage('field_invalid', "validation.field_invalid").replace("{0}", "Number")
            }
            break;
        case "validZipCode":
            valObj = {
                type: validation.type,
                message: getMessage('postal_code_invalid', "validation.postal_code_invalid")
            }
            break;
        case 'minLength':
            valObj = {
                type: validation.type,
                minLength: element?.fieldSetting?.minLength,
                message: getMessage('min_length_invalid', "validation.min_length_invalid")
                    .replace("{0}", fieldName)
                    .replace("{length}", element?.fieldSetting?.minLength)
            }
            break;
        case 'maxLength':
            valObj = {
                type: validation.type,
                maxLength: element?.fieldSetting?.maxLength,
                message: getMessage('max_length_invalid', "validation.max_length_invalid")
                    .replace("{0}", fieldName)
                    .replace("{length}", element?.fieldSetting?.maxLength)
            }
            break;
        case 'apaarId':
            valObj = {
                type: validation.type,
                length: element?.fieldSetting?.length,
                message: getMessage('apaar_id_invalid', "validation.apaar_id_invalid")
                    .replace("{0}", fieldName)
                    .replace("{length}", element?.fieldSetting?.length)
            }
            break;
        case 'maxDate':
            {
                const isTodayMax = element?.fieldSetting?.maxDate &&
                    new Date(element.fieldSetting.maxDate).toDateString() === new Date().toDateString();
                valObj = {
                    type: validation.type,
                    maxDate: element?.fieldSetting?.maxDate,
                    maxDateFrom: element?.fieldSetting?.maxDateFrom,
                    maxDaysOffset: element?.fieldSetting?.maxDaysOffset,
                    message: validation.message || (
                        isTodayMax
                            ? getMessage('future_date_invalid', "{0} cannot be a future date").replace('{0}', fieldName)
                            : getMessage('max_date_invalid', "{0} cannot be after maximum allowed date").replace('{0}', fieldName)
                    )
                };
            }
            break;
        case 'minDate':
            valObj = {
                type: validation.type,
                minDate: element?.fieldSetting?.minDate,
                minDateFrom: element?.fieldSetting?.minDateFrom,
                daysOffset: element?.fieldSetting?.daysOffset,
                message: validation.message || getMessage('min_date_invalid', "{0} cannot be before minimum allowed date").replace('{0}', fieldName)
            };
            break;
        case 'maskFormat': {
            const format = element?.fieldSetting?.masked?.format || '';
            valObj = {
                type: validation.type,
                format,
                message: (validation.message || getMessage('mask_format_invalid', "validation.mask_format_invalid"))
                    .replace("{0}", fieldName)
                    .replace("{format}", format)
            };
            break;
        }
        case 'image':
            valObj = {
                type: validation.type,
                message: ''
            };
            break;
        case 'file':
            valObj = {
                type: validation.type,
                message: ''
            };
            break;
        default:
            break;
    }

    if (['validEIN', 'validPhone', 'validFax'].includes(validation.type)) {
        valObj = {
            type: validation.type,
            message: getMessage('field_invalid', "validation.field_invalid").replace("{0}", fieldName)
        };
    }

    return valObj;
};